// INV31 (docs/build-briefs/INV31-set-starting-stock.md; DEC-288): the "Set
// starting stock" screen. One list of every active item, one starting-quantity
// control per item, a running count, one confirm step, and a per-item result.
// It calls INV7's opening-balance route once per item, three at a time, and
// reads which items already have ledger history from the new status route, so
// the screen never re-implements that rule. The logic it trusts (classifying an
// item, what each kind sends, the filters, the draft, the outcome mapping, the
// batch runner) is ui/src/startingStockModel.ts and is unit tested without a
// DOM.
//
// This is an admin screen (a keyboard and pointer may be assumed), built
// touch-first anyway: every target is at least 44px, nothing depends on hover,
// and there is NO key handler anywhere in this file. The only typed fields are
// the item search and, for an item with no bottle size, ounces; each has a
// touch alternative (category chips, and the -5, +5 and quick-add buttons).
//
// The screen is drawn into #admin-starting-stock-screen and, while it is open,
// the rest of Settings is hidden by a class on #admin-screen, so it reads as one
// full page with a back button to Inventory. Sign-out calls
// resetStartingStock() (admin.ts's invalidate()), which closes it and drops any
// answer still in flight so the next employee never inherits it.

import {
  getOpeningBalanceStatus,
  listLiquorProducts,
  recordOpeningBalanceMovement,
  setStartsEmpty,
  type OpeningBalanceHistory,
} from "./api.ts";
import { BOTTLE_TENTH_OPTIONS } from "./countWalk.ts";
import { hasPermission, type Session } from "./session.ts";
import {
  adjustOunces,
  buildRequest,
  classifyItem,
  classifySaveResponse,
  cleanDraft,
  countSummary,
  draftKey,
  entryDisplay,
  groupByCategory,
  parseDraft,
  runBatch,
  serializeDraft,
  visibleItems,
  type Entry,
  type ListFilter,
  type SaveOutcome,
  type StatusFilter,
  type StockItem,
  UNSIZED_SENTENCE,
} from "./startingStockModel.ts";
import { compareNames } from "./nameSort.ts";

// ---------------------------------------------------------------------
// Wiring supplied by admin.ts
// ---------------------------------------------------------------------

export interface StartingStockHooks {
  getSession: () => Session | undefined;
  /** Back to Settings > Inventory. */
  onClose: () => void;
  /** Something that changes the setup card or the callout happened. */
  onChanged: () => void;
  /** Leave for Inventory > Counts. */
  goToCounts: () => void;
  /** Leave for the item's own Stock & vendor tab, to set its bottle size. */
  setBottleSize: (itemId: string) => void;
  toast: (message: string, tone: "danger" | "success" | "neutral") => void;
}

let hooks: StartingStockHooks | undefined;

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) {
    throw new Error(`expected element for selector "${selector}"`);
  }
  return element;
}

const adminScreenEl = mustFind<HTMLElement>("#admin-screen");
const rootEl = mustFind<HTMLElement>("#admin-starting-stock-screen");
const calloutEl = mustFind<HTMLElement>("#admin-starting-stock-callout");
const calloutTitleEl = mustFind<HTMLElement>("#admin-starting-stock-callout-title");
const calloutOpenButton = mustFind<HTMLButtonElement>("#admin-starting-stock-callout-open");
const quietOpenButton = mustFind<HTMLButtonElement>("#admin-starting-stock-open");

const OPEN_CLASS = "admin-starting-stock-open";
const CONCURRENCY = 3;
const MOVEMENT_PERMISSION = "inv.movement.record";
const LIST_PERMISSION = "admin.liquor_product.view";

// ---------------------------------------------------------------------
// State
// ---------------------------------------------------------------------

type Phase = "loading" | "error" | "nopermission" | "ready" | "confirm" | "saving" | "result";

interface Batch {
  id: string;
  /** The items in this run, in list order. */
  order: string[];
  results: Map<string, SaveOutcome>;
  running: Set<string>;
  stopping: boolean;
  /** Items never sent because the run was stopped. */
  total: number;
}

interface State {
  session: Session;
  phase: Phase;
  items: StockItem[];
  statusById: Record<string, OpeningBalanceHistory>;
  startsEmpty: boolean;
  entries: Map<string, Entry>;
  openId: string | undefined;
  filter: ListFilter;
  /** A server message to show under a row that was refused with a fixable answer. */
  rowMessages: Map<string, string>;
  /** Items flipped to the ounces-only editor after a "no bottle size" answer. */
  forcedOunces: Set<string>;
  notice: string;
  batch: Batch | undefined;
  /** One batch id per Review tap, reused by Try again. */
  batchId: string | undefined;
  /** A location that is fenced for cutover answers every write the same way. */
  unavailable: boolean;
  checking: boolean;
}

let state: State | undefined;
/** Bumped by reset and by every open, so an answer that arrives after either is dropped. */
let generation = 0;
let entrySession: Session | undefined;
let storageWarned = false;

/** MGRDAYMAJ1 (manager-day-12): the items the callout counts, so Stock levels can say "No starting stock yet" for them instead
 * of Out. Set by every status read above, and empty whenever the callout would not show (the location starts empty, its initial
 * stock is complete, or the read failed), so an item that is really out of stock still reads Out. */
let noStartingStock: ReadonlySet<string> = new Set();
let noStartingStockListener: (() => void) | undefined;
export function noStartingStockIds(): ReadonlySet<string> {
  return noStartingStock;
}
export function onNoStartingStockChanged(listener: () => void): void {
  noStartingStockListener = listener;
}
function setNoStartingStock(next: ReadonlySet<string>): void {
  const same = next.size === noStartingStock.size && [...next].every((id) => noStartingStock.has(id));
  noStartingStock = next;
  if (!same) noStartingStockListener?.();
}
/** Entries just sent, kept only so the saving and result lists can still show
 * their amount after a saved item's draft entry is cleared. */
const savedEntries = new Map<string, Entry>();

function isCurrent(gen: number): boolean {
  return gen === generation && state !== undefined;
}

// ---------------------------------------------------------------------
// Tiny DOM helpers
// ---------------------------------------------------------------------

type Child = Node | string | null | undefined | false;

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Record<string, string | boolean | undefined> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(props)) {
    if (value === undefined || value === false) continue;
    if (name === "class") element.className = String(value);
    else if (name === "testid") element.dataset["testid"] = String(value);
    else if (name.startsWith("data-")) element.setAttribute(name, String(value));
    else if (value === true) element.setAttribute(name, "");
    else element.setAttribute(name, String(value));
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    element.append(typeof child === "string" ? document.createTextNode(child) : child);
  }
  return element;
}

function button(
  label: string,
  props: Record<string, string | boolean | undefined>,
  onClick: () => void,
): HTMLButtonElement {
  const element = h("button", { type: "button", ...props }, label);
  element.addEventListener("click", onClick);
  return element;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function currentItem(id: string): StockItem | undefined {
  const s = state;
  if (s === undefined) return undefined;
  const item = s.items.find((candidate) => candidate.id === id);
  if (item === undefined) return undefined;
  if (s.forcedOunces.has(id) && item.kind !== "unsized") {
    return {
      ...item,
      kind: "unsized",
      containerWord: "",
      containerUnit: null,
      sizeLabel: "",
      ouncesPerContainer: null,
      meta: `${item.categoryName} · no bottle size yet`,
    };
  }
  return item;
}

function effectiveItems(): StockItem[] {
  const s = state;
  if (s === undefined) return [];
  return s.items.map((item) => currentItem(item.id) ?? item);
}

// ---------------------------------------------------------------------
// The draft: kept per tenant and per person on this device
// ---------------------------------------------------------------------

function persistDraft(): void {
  const s = state;
  if (s === undefined) return;
  const key = draftKey(s.session.tenantId, s.session.userId);
  try {
    if (s.entries.size === 0) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, serializeDraft(s.entries));
    }
  } catch (error) {
    // Storage can be full or blocked (a private window). The screen still
    // works; say so once instead of pretending the draft is kept.
    if (!(error instanceof DOMException)) throw error;
    if (!storageWarned) {
      storageWarned = true;
      s.notice =
        "Your entries could not be kept on this device. Finish and save before you close this page.";
      renderNotice();
    }
  }
}

function readStoredDraft(session: Session): Map<string, Entry> {
  try {
    return parseDraft(window.localStorage.getItem(draftKey(session.tenantId, session.userId)));
  } catch (error) {
    if (!(error instanceof DOMException)) throw error;
    return new Map();
  }
}

// ---------------------------------------------------------------------
// Opening and closing
// ---------------------------------------------------------------------

export function configureStartingStock(next: StartingStockHooks): void {
  hooks = next;
  const open = (): void => {
    const session = hooks?.getSession();
    if (session !== undefined) {
      void openStartingStockScreen(session);
    }
  };
  calloutOpenButton.addEventListener("click", open);
  quietOpenButton.addEventListener("click", open);
}

function closeScreenDom(): void {
  adminScreenEl.classList.remove(OPEN_CLASS);
  rootEl.hidden = true;
  rootEl.replaceChildren();
}

/** Sign-out and teardown: close the screen, drop everything held, and make any
 * read still in flight land nowhere. The draft stays on the device, keyed to the
 * person who made it. */
export function resetStartingStock(): void {
  generation += 1;
  state = undefined;
  savedEntries.clear();
  entrySession = undefined;
  noStartingStock = new Set();
  calloutWanted = false;
  entryKnown = false;
  listIsEmpty = false;
  closeScreenDom();
  calloutEl.hidden = true;
  calloutTitleEl.textContent = "";
  quietOpenButton.hidden = true;
}

function closeStartingStockScreen(): void {
  generation += 1;
  state = undefined;
  savedEntries.clear();
  closeScreenDom();
  hooks?.onClose();
}

/** The page scrolls inside #app-shell, not the window, so both are reset. */
function scrollScreenToTop(): void {
  document.querySelector<HTMLElement>("#app-shell")?.scrollTo({ top: 0, behavior: "instant" });
  window.scrollTo(0, 0);
}

export async function openStartingStockScreen(session: Session): Promise<void> {
  generation += 1;
  const gen = generation;
  const mayWrite = hasPermission(session, MOVEMENT_PERMISSION);
  const mayList = hasPermission(session, LIST_PERMISSION);
  state = {
    session,
    phase: mayWrite && mayList ? "loading" : "nopermission",
    items: [],
    statusById: {},
    startsEmpty: false,
    entries: new Map(),
    openId: undefined,
    filter: { q: "", category: "all", status: "all" },
    rowMessages: new Map(),
    forcedOunces: new Set(),
    notice: "",
    batch: undefined,
    batchId: undefined,
    unavailable: false,
    checking: false,
  };
  adminScreenEl.classList.add(OPEN_CLASS);
  rootEl.hidden = false;
  renderScreen();
  scrollScreenToTop();
  if (state.phase !== "loading") return;
  await loadScreenData(gen);
}

async function loadScreenData(gen: number): Promise<void> {
  const s = state;
  if (s === undefined) return;
  const { apiBaseUrl, tenantId } = s.session;
  const [status, products] = await Promise.all([
    getOpeningBalanceStatus(apiBaseUrl, tenantId),
    listLiquorProducts(apiBaseUrl, tenantId, { activeOnly: true }),
  ]);
  if (!isCurrent(gen) || state === undefined) return;
  if (status.status === 401 || status.status === 403 || products.status === 403) {
    state.phase = "nopermission";
    renderScreen();
    return;
  }
  if (status.status !== 200 || products.status !== 200) {
    state.phase = "error";
    renderScreen();
    return;
  }
  const statusById: Record<string, OpeningBalanceHistory> = {};
  for (const row of status.body.products) {
    statusById[row.liquorProductId] = row.history;
  }
  // The list is the existing admin route; the status read says which of those
  // are active items of this location. An active item the status read has not
  // seen yet (created a moment ago) reads as "none"; the server still decides
  // at save time.
  state.items = products.body.liquorProducts
    .filter((product) => product.active)
    .map((product) => classifyItem(product));
  state.statusById = statusById;
  state.startsEmpty = status.body.startsEmpty;

  const cleaned = cleanDraft(readStoredDraft(state.session), state.items, statusById);
  state.entries = cleaned.kept;
  if (cleaned.droppedForHistory > 0 || cleaned.droppedOther > 0) {
    const dropped = cleaned.droppedForHistory + cleaned.droppedOther;
    state.notice =
      `${plural(dropped, "saved entry was", "saved entries were")} dropped because ` +
      (cleaned.droppedForHistory > 0
        ? "the item now has stock history. Use a count to change it."
        : "the item is no longer on the list.");
    persistDraft();
  }
  state.phase = "ready";
  renderScreen();
}

// ---------------------------------------------------------------------
// The entry points: the callout, the quiet button and the setup step
// ---------------------------------------------------------------------

/** The permission half of the entry points, with no fetch: safe to call on every
 * permission refresh. A session without inv.movement.record sees neither. What
 * a permitted session sees is decided by the status read below. */
export function applyStartingStockEntryGating(session: Session): void {
  if (!hasPermission(session, MOVEMENT_PERMISSION)) {
    quietOpenButton.hidden = true;
    calloutEl.hidden = true;
    entryKnown = false;
  }
}

/** Shows or hides the two Inventory entry points. Called when Inventory opens
 * and after anything that changes the answer. The accent callout shows while
 * some item has no history and the location has not said it starts empty; the
 * quiet button in the header takes over whenever the callout is not showing, so
 * the screen is always one tap away and the two never sit side by side. A
 * session without the permission sees neither. */
export async function refreshStartingStockEntry(session: Session): Promise<void> {
  entrySession = session;
  applyStartingStockEntryGating(session);
  if (!hasPermission(session, MOVEMENT_PERMISSION)) {
    return;
  }
  const gen = generation;
  const result = await getOpeningBalanceStatus(session.apiBaseUrl, session.tenantId);
  if (gen !== generation || entrySession !== session) return;
  if (result.status !== 200) {
    // An unreadable answer shows no callout rather than a wrong count; the
    // quiet button still opens the screen, which reads for itself.
    calloutWanted = false;
    entryKnown = false;
    calloutEl.hidden = true;
    quietOpenButton.hidden = false;
    setNoStartingStock(new Set());
    return;
  }
  // INV38: once the location's initial stock is in, neither entry point shows again, even when a new
  // item is added later. The screen itself still opens by direct navigation.
  if (result.body.initialStockCompleted) {
    calloutWanted = false;
    calloutEl.hidden = true;
    quietOpenButton.hidden = true;
    entryKnown = false;
    setNoStartingStock(new Set());
    return;
  }
  entryKnown = true;
  const lacking = result.body.products.filter((row) => row.history === "none").length;
  calloutWanted = lacking > 0 && !result.body.startsEmpty;
  setNoStartingStock(
    result.body.startsEmpty
      ? new Set()
      : new Set(result.body.products.filter((row) => row.history === "none").map((row) => row.liquorProductId)),
  );
  if (calloutWanted) {
    calloutTitleEl.textContent = `${plural(lacking, "item has", "items have")} no starting stock yet`;
  }
  applyCalloutVisibility();
}

/** HIGFIX4A (F-23): the callout and the list's own "No inventory items" line never show together; the empty list wins. */
let calloutWanted = false;
let entryKnown = false;
let listIsEmpty = false;
function applyCalloutVisibility(): void {
  if (!entryKnown) return;
  const show = calloutWanted && !listIsEmpty;
  calloutEl.hidden = !show;
  quietOpenButton.hidden = show;
}
export function setStockListEmpty(empty: boolean): void {
  if (listIsEmpty === empty) return;
  listIsEmpty = empty;
  applyCalloutVisibility();
}

/** For the setup card: the per-item history and the starts-empty answer, or
 * undefined when the read did not succeed (the step then reads unknown). */
export async function readStartingStockStatus(session: Session): Promise<
  | {
      products: { liquorProductId: string; history: OpeningBalanceHistory }[];
      startsEmpty: boolean;
      initialStockCompleted: boolean;
    }
  | undefined
> {
  const result = await getOpeningBalanceStatus(session.apiBaseUrl, session.tenantId);
  if (result.status !== 200) return undefined;
  return {
    products: result.body.products,
    startsEmpty: result.body.startsEmpty,
    initialStockCompleted: result.body.initialStockCompleted,
  };
}

// ---------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------

function renderScreen(): void {
  const s = state;
  if (s === undefined) return;
  const header = h(
    "div",
    { class: "ss-top" },
    button(
      "‹ Inventory",
      { class: "btn-secondary btn-compact ss-back", testid: "starting-stock-back" },
      () => closeStartingStockScreen(),
    ),
  );
  const title = h(
    "div",
    { class: "ss-heading" },
    // UIFIX2 (SF-11): the crumb names the path the person took: Settings, Inventory items when opened from the
    // Settings checklist or callout, or the Inventory menu when opened from there (the operations view).
    h(
      "div",
      { class: "ss-crumb", testid: "starting-stock-crumb" },
      document.getElementById("admin-screen")?.classList.contains("admin-ops-mode") === true
        ? "Inventory"
        : "Settings › Inventory items",
    ),
    h("h1", { class: "ss-title" }, "Set starting stock"),
  );
  rootEl.replaceChildren(header, title);

  if (s.phase === "loading") {
    rootEl.append(
      h("p", { class: "tabs-loading", testid: "starting-stock-loading" }, "Loading items…"),
    );
    return;
  }
  if (s.phase === "nopermission") {
    rootEl.append(
      h(
        "div",
        { class: "ss-empty", testid: "starting-stock-no-permission" },
        h("b", {}, "You need permission to do this"),
        "Setting starting stock needs the right to record stock changes and to see the item list. Ask an owner or manager.",
        h(
          "div",
          { class: "ss-empty-actions" },
          button("Back to Inventory", { class: "btn-secondary" }, () => closeStartingStockScreen()),
        ),
      ),
    );
    return;
  }
  if (s.phase === "error") {
    rootEl.append(
      h(
        "div",
        { class: "ss-empty", testid: "starting-stock-error" },
        h("b", {}, "The items could not be loaded"),
        "Check the connection and try again. Nothing has been changed.",
        h(
          "div",
          { class: "ss-empty-actions" },
          button("Try again", { class: "btn-primary", testid: "starting-stock-reload" }, () => {
            const gen = generation;
            s.phase = "loading";
            renderScreen();
            void loadScreenData(gen);
          }),
        ),
      ),
    );
    return;
  }

  const items = effectiveItems();
  if (items.length === 0) {
    rootEl.append(
      h(
        "div",
        { class: "ss-empty", testid: "starting-stock-no-items" },
        h("b", {}, "There are no items yet"),
        "Add the items this location stocks first, then come back to set what is on the shelf.",
        h(
          "div",
          { class: "ss-empty-actions" },
          button("Back to Inventory", { class: "btn-secondary" }, () => closeStartingStockScreen()),
        ),
      ),
    );
    return;
  }
  const counts = countSummary(items, s.statusById, s.entries);
  if (counts.alreadySet === counts.total) {
    rootEl.append(
      h(
        "div",
        { class: "ss-empty", testid: "starting-stock-all-set" },
        h("b", {}, "Starting stock is set for every item"),
        `${counts.total} of ${counts.total} items have stock history. To change an amount now, run a count.`,
        h(
          "div",
          { class: "ss-empty-actions" },
          button(
            "Start a count",
            { class: "btn-primary", testid: "starting-stock-start-count" },
            () => {
              closeStartingStockScreen();
              hooks?.goToCounts();
            },
          ),
        ),
      ),
    );
    return;
  }

  rootEl.append(
    h(
      "p",
      { class: "ss-lede" },
      "Enter what is on the shelf right now, before the first sale. Each item can be set once. Leave an item blank to set it later.",
    ),
    h(
      "div",
      { class: "ss-rule" },
      h(
        "span",
        {},
        h("b", {}, "Blank is not zero."),
        " Tap ",
        h("b", {}, "We have none"),
        " when the shelf is really empty. Items you skip stay open.",
      ),
    ),
    h("div", { id: "ss-choice", class: "ss-choice" }),
    h("div", {
      id: "ss-notice",
      class: "ss-notice",
      testid: "starting-stock-notice",
      hidden: true,
    }),
    buildFilterShell(),
    h("div", { id: "ss-status-banner" }),
    h("div", { id: "ss-list", testid: "starting-stock-list" }),
    h("div", { id: "ss-bar-slot" }),
    h("div", { id: "ss-sheet-slot" }),
  );
  renderChoice();
  renderNotice();
  renderFilterControls();
  renderList();
  renderBar();
  renderSheet();
}

function renderChoice(): void {
  const s = state;
  const slot = rootEl.querySelector<HTMLElement>("#ss-choice");
  if (s === undefined || slot === null) return;
  if (s.startsEmpty) {
    slot.replaceChildren(
      h(
        "div",
        { class: "ss-choice-box ss-choice-on", testid: "starting-stock-empty-marked" },
        h(
          "span",
          {},
          h("b", {}, "This location starts empty."),
          " The setup step is marked done. You can still set starting stock for any item below.",
        ),
        button(
          "Undo",
          { class: "btn-secondary btn-compact", testid: "starting-stock-starts-empty-undo" },
          () => void chooseStartsEmpty(false),
        ),
      ),
    );
    return;
  }
  slot.replaceChildren(
    h(
      "div",
      { class: "ss-choice-box" },
      h(
        "span",
        {},
        h("b", {}, "Nothing on the shelf yet?"),
        " If this location starts with no stock, say so. It marks this setup step done and writes nothing.",
      ),
      button(
        "This location starts empty",
        { class: "btn-secondary btn-compact", testid: "starting-stock-starts-empty" },
        () => void chooseStartsEmpty(true),
      ),
    ),
  );
}

async function chooseStartsEmpty(value: boolean): Promise<void> {
  const s = state;
  if (s === undefined) return;
  const gen = generation;
  const result = await setStartsEmpty(s.session.apiBaseUrl, s.session.tenantId, value);
  if (!isCurrent(gen) || state === undefined) return;
  if (result.status !== 200) {
    hooks?.toast(
      result.status === 403
        ? "You do not have permission to change this."
        : "Could not save that. Check the connection and try again.",
      "danger",
    );
    return;
  }
  state.startsEmpty = result.body.startsEmpty;
  renderChoice();
  hooks?.toast(
    state.startsEmpty
      ? "Marked: this location starts empty."
      : "No longer marked as starting empty.",
    "success",
  );
  hooks?.onChanged();
}

function renderNotice(): void {
  const s = state;
  const slot = rootEl.querySelector<HTMLElement>("#ss-notice");
  if (s === undefined || slot === null) return;
  slot.textContent = s.notice;
  slot.hidden = s.notice.length === 0;
}

// ---- filters ---------------------------------------------------------

function buildFilterShell(): HTMLElement {
  const search = h("input", {
    class: "ss-search",
    id: "ss-search",
    type: "search",
    inputmode: "search",
    autocomplete: "off",
    placeholder: "Search items",
    "aria-label": "Search items",
    testid: "starting-stock-search",
  });
  search.addEventListener("input", () => {
    if (state === undefined) return;
    state.filter = { ...state.filter, q: search.value };
    renderList();
  });
  return h(
    "div",
    { class: "ss-filters" },
    h(
      "div",
      { class: "ss-filter-row" },
      search,
      h("div", { id: "ss-seg", class: "ss-seg", role: "group", "aria-label": "Show" }),
    ),
    h("div", { id: "ss-chips", class: "ss-chips", role: "group", "aria-label": "Category" }),
  );
}

function renderFilterControls(): void {
  const s = state;
  const seg = rootEl.querySelector<HTMLElement>("#ss-seg");
  const chips = rootEl.querySelector<HTMLElement>("#ss-chips");
  if (s === undefined || seg === null || chips === null) return;
  const items = effectiveItems();
  const counts = countSummary(items, s.statusById, s.entries);

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "left", label: `Left to enter (${counts.left})` },
    { value: "entered", label: `Entered (${counts.entered})` },
    { value: "done", label: `Already set (${counts.alreadySet})` },
  ];
  seg.replaceChildren(
    ...statusOptions.map((option) =>
      button(
        option.label,
        {
          class: "ss-seg-button",
          "aria-pressed": String(s.filter.status === option.value),
          testid: `starting-stock-status-${option.value}`,
        },
        () => {
          if (state === undefined) return;
          state.filter = { ...state.filter, status: option.value };
          renderFilterControls();
          renderList();
        },
      ),
    ),
  );

  const categories = new Map<string, { name: string; count: number }>();
  for (const item of items) {
    const entry = categories.get(item.categoryKey);
    if (entry === undefined) {
      categories.set(item.categoryKey, { name: item.categoryName, count: 1 });
    } else {
      entry.count += 1;
    }
  }
  const ordered = [...categories.entries()].sort((a, b) => compareNames(a[1].name, b[1].name));
  const makeChip = (key: string, name: string, count: number): HTMLButtonElement => {
    const element = h(
      "button",
      {
        type: "button",
        class: "ss-chip",
        "aria-pressed": String(s.filter.category === key),
        testid: `starting-stock-category-${key}`,
        "data-fk": `cat-${key}`,
      },
      name,
      h("span", { class: "ss-chip-n" }, String(count)),
    );
    element.addEventListener("click", () => {
      if (state === undefined) return;
      state.filter = { ...state.filter, category: key };
      renderFilterControls();
      renderList();
    });
    return element;
  };
  chips.replaceChildren(
    makeChip("all", "All", items.length),
    ...ordered.map(([key, value]) => makeChip(key, value.name, value.count)),
  );
}

// ---- list ------------------------------------------------------------

function renderList(): void {
  const s = state;
  const list = rootEl.querySelector<HTMLElement>("#ss-list");
  const banner = rootEl.querySelector<HTMLElement>("#ss-status-banner");
  if (s === undefined || list === null || banner === null) return;
  const items = effectiveItems();
  banner.replaceChildren(
    ...(s.filter.status === "done"
      ? [
          h(
            "div",
            { class: "ss-rule", testid: "starting-stock-locked-banner" },
            h(
              "span",
              {},
              h("b", {}, "Why these are locked."),
              " A starting amount can only be set before an item has any stock history. Once an item has been sold, delivered or counted, change its amount with a count.",
            ),
          ),
        ]
      : []),
  );
  const shown = visibleItems(items, s.statusById, s.entries, s.filter);
  if (shown.length === 0) {
    list.replaceChildren(
      h(
        "div",
        { class: "ss-empty", testid: "starting-stock-no-match" },
        h("b", {}, "No items match"),
        "Clear the search or pick another category.",
      ),
    );
    return;
  }
  const groups = groupByCategory(shown, s.statusById, s.entries);
  const nodes: Node[] = [];
  for (const group of groups) {
    const groupNote =
      s.filter.status === "done"
        ? `${group.items.length} locked`
        : `${group.entered} of ${group.open} entered`;
    nodes.push(
      h(
        "div",
        { class: "ss-cat-head", testid: "starting-stock-group" },
        h("h2", {}, group.categoryName),
        h("span", { "data-group-count": group.categoryKey }, groupNote),
      ),
      h("ul", { class: "ss-rows" }, ...group.items.map((item) => buildRow(item))),
    );
  }
  list.replaceChildren(...nodes);
}

function lockedTag(history: OpeningBalanceHistory): { label: string; why: string } {
  return history === "opening_balance"
    ? {
        label: "Starting stock set",
        why: "Starting stock was already set here. To change the amount, use a count.",
      }
    : {
        label: "Has history",
        why: "Already has stock history (a sale, delivery or count). To change the amount, use a count.",
      };
}

function buildRow(item: StockItem): HTMLElement {
  const s = state;
  if (s === undefined) return h("li", {});
  const history = s.statusById[item.id] ?? "none";
  if (history !== "none") {
    const tag = lockedTag(history);
    return h(
      "li",
      {
        class: "ss-row ss-row-done",
        testid: "starting-stock-row",
        "data-id": item.id,
        "data-state": history === "opening_balance" ? "set" : "history",
      },
      h(
        "div",
        { class: "ss-row-head" },
        h(
          "span",
          { class: "ss-row-main" },
          h("span", { class: "ss-row-name", testid: "starting-stock-row-name" }, item.name),
          h("span", { class: "ss-row-meta" }, item.meta),
        ),
        h(
          "span",
          { class: "ss-row-state ss-row-state-done" },
          h("span", { class: "ss-tag ss-tag-done", testid: "starting-stock-row-tag" }, tag.label),
          h("span", { class: "ss-done-why" }, tag.why),
        ),
      ),
    );
  }
  const entry = s.entries.get(item.id);
  const display = entryDisplay(item, entry);
  const open = s.openId === item.id;
  const head = h(
    "button",
    {
      type: "button",
      class: "ss-row-head ss-row-toggle",
      "aria-expanded": String(open),
      testid: "starting-stock-row-toggle",
      "data-fk": `toggle-${item.id}`,
    },
    h(
      "span",
      { class: "ss-row-main" },
      h("span", { class: "ss-row-name", testid: "starting-stock-row-name" }, item.name),
      h("span", { class: "ss-row-meta" }, item.meta),
    ),
    h(
      "span",
      { class: "ss-row-state", "data-row-state": "" },
      ...(display === null
        ? [h("span", { class: "ss-todo" }, "Enter")]
        : [
            h("span", { class: "ss-val", testid: "starting-stock-row-value" }, display.text),
            h("span", { class: "ss-val-sub", testid: "starting-stock-row-sub" }, display.sub),
          ]),
    ),
  );
  head.addEventListener("click", () => {
    if (state === undefined) return;
    const previous = state.openId;
    state.openId = previous === item.id ? undefined : item.id;
    if (previous !== undefined && previous !== item.id) refreshRow(previous);
    refreshRow(item.id);
  });
  const message = s.rowMessages.get(item.id);
  return h(
    "li",
    {
      class: `ss-row${display === null ? "" : " ss-row-entered"}${open ? " ss-row-open" : ""}`,
      testid: "starting-stock-row",
      "data-id": item.id,
      "data-state": display === null ? "blank" : "entered",
    },
    head,
    message === undefined
      ? null
      : h("div", { class: "ss-row-msg", testid: "starting-stock-row-message" }, message),
    open ? buildEditor(item) : null,
  );
}

/** Rebuilds one row in place, keeping the person's place on the page and the
 * focus on the same control. */
function refreshRow(id: string): void {
  const item = currentItem(id);
  const existing = rootEl.querySelector<HTMLElement>(`li.ss-row[data-id="${CSS.escape(id)}"]`);
  if (item === undefined || existing === null) {
    renderList();
    return;
  }
  const focusKey = (document.activeElement as HTMLElement | null)?.dataset["fk"];
  existing.replaceWith(buildRow(item));
  if (focusKey !== undefined) {
    rootEl.querySelector<HTMLElement>(`[data-fk="${CSS.escape(focusKey)}"]`)?.focus();
  }
  afterEntryChange();
}

function afterEntryChange(): void {
  renderFilterControls();
  renderBar();
  updateGroupCounts();
}

function updateGroupCounts(): void {
  const s = state;
  if (s === undefined) return;
  const items = effectiveItems();
  const shown = visibleItems(items, s.statusById, s.entries, s.filter);
  for (const group of groupByCategory(shown, s.statusById, s.entries)) {
    const span = rootEl.querySelector<HTMLElement>(
      `[data-group-count="${CSS.escape(group.categoryKey)}"]`,
    );
    if (span !== null) {
      span.textContent =
        s.filter.status === "done"
          ? `${group.items.length} locked`
          : `${group.entered} of ${group.open} entered`;
    }
  }
}

/** Updates the row's right-hand value without rebuilding it, so typing in the
 * ounces box keeps its focus. */
function updateRowValue(id: string): void {
  const s = state;
  const item = currentItem(id);
  const row = rootEl.querySelector<HTMLElement>(`li.ss-row[data-id="${CSS.escape(id)}"]`);
  if (s === undefined || item === undefined || row === null) return;
  const display = entryDisplay(item, s.entries.get(id));
  const slot = row.querySelector<HTMLElement>("[data-row-state]");
  if (slot !== null) {
    slot.replaceChildren(
      ...(display === null
        ? [h("span", { class: "ss-todo" }, "Enter")]
        : [
            h("span", { class: "ss-val", testid: "starting-stock-row-value" }, display.text),
            h("span", { class: "ss-val-sub", testid: "starting-stock-row-sub" }, display.sub),
          ]),
    );
  }
  row.classList.toggle("ss-row-entered", display !== null);
  row.dataset["state"] = display === null ? "blank" : "entered";
  const total = row.querySelector<HTMLElement>("[data-ss-total]");
  if (total !== null) {
    total.replaceChildren(...totalLine(item, s.entries.get(id)));
  }
  afterEntryChange();
}

// ---- the editor ------------------------------------------------------

function setEntry(id: string, entry: Entry | undefined): void {
  const s = state;
  if (s === undefined) return;
  if (entry === undefined) {
    s.entries.delete(id);
  } else {
    s.entries.set(id, entry);
  }
  s.rowMessages.delete(id);
  persistDraft();
}

function totalLine(item: StockItem, entry: Entry | undefined): (Node | string)[] {
  const display = entryDisplay(item, entry);
  if (display === null) {
    return [
      h(
        "span",
        { class: "ss-total-hint" },
        "Tap a number above. Nothing is saved until you review.",
      ),
    ];
  }
  if (item.kind === "bottle" || item.kind === "keg") {
    const of =
      item.kind === "keg" ? ` (${item.sizeLabel})` : ` of ${item.sizeLabel} ${item.containerWord}s`;
    return [
      h("b", { testid: "starting-stock-total" }, display.text),
      " ",
      h("span", { class: "ss-total-oz" }, display.sub.length > 0 ? `= ${display.sub}${of}` : ""),
    ];
  }
  return [h("b", { testid: "starting-stock-total" }, display.text)];
}

function stepper(
  value: string,
  down: { aria: string; onClick: () => void; text: string },
  up: { aria: string; onClick: () => void; text: string },
  fk: string,
  centre?: HTMLElement,
): HTMLElement {
  return h(
    "div",
    { class: "ss-stepper" },
    button(
      down.text,
      {
        class: "ss-step-button",
        "aria-label": down.aria,
        "data-fk": `${fk}-down`,
        testid: `${fk}-down`,
      },
      down.onClick,
    ),
    centre ?? h("span", { class: "ss-num", testid: `${fk}-value`, "aria-live": "polite" }, value),
    button(
      up.text,
      { class: "ss-step-button", "aria-label": up.aria, "data-fk": `${fk}-up`, testid: `${fk}-up` },
      up.onClick,
    ),
  );
}

function buildEditor(item: StockItem): HTMLElement {
  const s = state;
  if (s === undefined) return h("div", {});
  const id = item.id;
  const entry = s.entries.get(id);
  const blocks: HTMLElement[] = [];

  if (item.kind === "bottle" || item.kind === "keg") {
    const word = item.containerWord;
    const container = entry !== undefined && entry.type === "container" ? entry : undefined;
    const whole = container?.whole ?? 0;
    const tenth = container?.tenth ?? 0;
    const change = (next: { whole: number; tenth: number }): void => {
      setEntry(id, { type: "container", ...next });
      refreshRow(id);
    };
    blocks.push(
      h(
        "div",
        { class: "ss-ed-grid" },
        h(
          "div",
          { class: "ss-ed-block" },
          h("span", { class: "ss-ed-label" }, `Full ${word}s`),
          stepper(
            String(whole),
            {
              text: "−",
              aria: `One fewer full ${word}`,
              onClick: () => change({ whole: Math.max(0, whole - 1), tenth }),
            },
            {
              text: "+",
              aria: `One more full ${word}`,
              onClick: () => change({ whole: whole + 1, tenth }),
            },
            "ss-whole",
          ),
        ),
        h(
          "div",
          { class: "ss-ed-block" },
          h("span", { class: "ss-ed-label" }, `Partial ${word}, in tenths`),
          h(
            "div",
            { class: "ss-tenths", role: "group", "aria-label": `Partial ${word}, in tenths` },
            ...BOTTLE_TENTH_OPTIONS.map((value) =>
              button(
                value === 0 ? "0" : `.${value}`,
                {
                  class: "ss-chip ss-tenth",
                  "aria-pressed": String(container !== undefined && container.tenth === value),
                  testid: `starting-stock-tenth-${value}`,
                  "data-fk": `tenth-${value}`,
                },
                () => change({ whole, tenth: value }),
              ),
            ),
          ),
        ),
      ),
    );
  } else if (item.kind === "unit") {
    const units = entry !== undefined && entry.type === "units" ? entry.units : 0;
    const add = (delta: number): void => {
      setEntry(id, { type: "units", units: Math.max(0, units + delta) });
      refreshRow(id);
    };
    blocks.push(
      h(
        "div",
        { class: "ss-ed-grid" },
        h(
          "div",
          { class: "ss-ed-block" },
          h("span", { class: "ss-ed-label" }, "Units on the shelf"),
          stepper(
            String(units),
            { text: "−", aria: "One fewer unit", onClick: () => add(-1) },
            { text: "+", aria: "One more unit", onClick: () => add(1) },
            "ss-units",
          ),
        ),
        h(
          "div",
          { class: "ss-ed-block" },
          h("span", { class: "ss-ed-label" }, "Add a pack"),
          h(
            "div",
            { class: "ss-quick" },
            ...[6, 12, 24].map((n) =>
              button(
                `+${n}`,
                {
                  class: "ss-chip",
                  testid: `starting-stock-units-add-${n}`,
                  "data-fk": `units-add-${n}`,
                },
                () => add(n),
              ),
            ),
          ),
        ),
      ),
    );
  } else {
    const text = entry !== undefined && entry.type === "ounces" ? entry.text : "";
    const input = h("input", {
      class: "ss-oz-field",
      type: "text",
      inputmode: "decimal",
      pattern: "[0-9]*[.]?[0-9]*",
      autocomplete: "off",
      "aria-label": "Ounces",
      value: text,
      testid: "starting-stock-ounces",
      "data-fk": "oz-input",
    });
    input.addEventListener("input", () => {
      const value = input.value.trim();
      setEntry(id, value.length === 0 ? undefined : { type: "ounces", text: value });
      updateRowValue(id);
    });
    const adjust = (delta: number): void => {
      const current = state?.entries.get(id);
      const base = current !== undefined && current.type === "ounces" ? current.text : "";
      setEntry(id, { type: "ounces", text: adjustOunces(base, delta) });
      refreshRow(id);
    };
    blocks.push(
      h(
        "div",
        { class: "ss-note", testid: "starting-stock-unsized-note" },
        h(
          "span",
          { class: "ss-note-text" },
          h("b", {}, "This item has no bottle size yet."),
          UNSIZED_SENTENCE.slice("This item has no bottle size yet.".length),
        ),
        button(
          "Set the bottle size",
          { class: "btn-secondary", testid: "starting-stock-set-size" },
          () => {
            closeStartingStockScreen();
            hooks?.setBottleSize(id);
          },
        ),
      ),
      h(
        "div",
        { class: "ss-ed-grid" },
        h(
          "div",
          { class: "ss-ed-block" },
          h("span", { class: "ss-ed-label" }, "Ounces on the shelf"),
          h(
            "div",
            { class: "ss-stepper" },
            button(
              "−5",
              {
                class: "ss-step-button",
                "aria-label": "Five fewer ounces",
                testid: "starting-stock-oz-minus-5",
                "data-fk": "oz-minus",
              },
              () => adjust(-5),
            ),
            input,
            button(
              "+5",
              {
                class: "ss-step-button",
                "aria-label": "Five more ounces",
                testid: "starting-stock-oz-plus-5",
                "data-fk": "oz-plus",
              },
              () => adjust(5),
            ),
            h("span", { class: "ss-unit" }, "oz"),
          ),
        ),
        h(
          "div",
          { class: "ss-ed-block" },
          h("span", { class: "ss-ed-label" }, "Quick add"),
          h(
            "div",
            { class: "ss-quick" },
            ...[1, 8, 25].map((n) =>
              button(
                `+${n} oz`,
                {
                  class: "ss-chip",
                  testid: `starting-stock-oz-add-${n}`,
                  "data-fk": `oz-add-${n}`,
                },
                () => adjust(n),
              ),
            ),
          ),
        ),
      ),
    );
  }

  if (item.kind !== "unsized") {
    blocks.push(h("div", { class: "ss-total", "data-ss-total": "" }, ...totalLine(item, entry)));
  }
  blocks.push(
    h(
      "div",
      { class: "ss-ed-actions" },
      button("We have none", { class: "btn-secondary", testid: "starting-stock-none" }, () => {
        setEntry(id, { type: "zero" });
        refreshRow(id);
      }),
      entry === undefined
        ? null
        : button(
            "Clear this one",
            { class: "btn-secondary ss-danger-quiet", testid: "starting-stock-clear" },
            () => {
              setEntry(id, undefined);
              refreshRow(id);
            },
          ),
      h("span", { class: "ss-grow" }),
      button("Done", { class: "btn-primary ss-done-button", testid: "starting-stock-done" }, () => {
        if (state === undefined) return;
        state.openId = undefined;
        refreshRow(id);
      }),
    ),
  );
  return h(
    "div",
    { class: "ss-editor", testid: "starting-stock-editor", "data-ed": id },
    ...blocks,
  );
}

// ---- the location ---------------------------------------------------------

function renderBar(): void {
  const s = state;
  const slot = rootEl.querySelector<HTMLElement>("#ss-bar-slot");
  if (s === undefined || slot === null) return;
  const counts = countSummary(effectiveItems(), s.statusById, s.entries);
  const percent =
    counts.entered + counts.left > 0
      ? Math.round((counts.entered * 100) / (counts.entered + counts.left))
      : 0;
  const review = h(
    "button",
    {
      type: "button",
      class: "btn-primary ss-review",
      testid: "starting-stock-review",
      disabled: counts.entered === 0 || s.checking,
    },
    counts.entered === 0 ? "Enter an amount to continue" : `Review and save ${counts.entered}`,
  );
  review.addEventListener("click", () => void handleReview());
  const bar = h(
    "div",
    { class: "ss-bar", id: "ss-bar", testid: "starting-stock-bar" },
    h(
      "div",
      { class: "ss-bar-in" },
      h(
        "div",
        { class: "ss-counts" },
        h(
          "div",
          { class: "ss-count" },
          h("b", { testid: "starting-stock-count-entered" }, String(counts.entered)),
          h("span", {}, "entered"),
        ),
        h(
          "div",
          { class: "ss-count" },
          h("b", { testid: "starting-stock-count-left" }, String(counts.left)),
          h("span", {}, "left to enter"),
        ),
        h(
          "div",
          { class: "ss-count ss-count-muted" },
          h("b", { testid: "starting-stock-count-set" }, String(counts.alreadySet)),
          h("span", {}, "already set"),
        ),
        h(
          "div",
          { class: "ss-meter", role: "img", "aria-label": `${percent}% of items entered` },
          h("i", { style: `width:${percent}%` }),
        ),
      ),
      review,
    ),
  );
  slot.replaceChildren(bar);
  applyKeyboardHide();
}

/** The sticky bar must not eat the space left above the on-screen keyboard: it
 * hides while a text field is focused AND the visible viewport has shrunk. A
 * desktop browser never shrinks the viewport, so the location stays there. */
function applyKeyboardHide(): void {
  const bar = rootEl.querySelector<HTMLElement>("#ss-bar");
  if (bar === null) return;
  const active = document.activeElement;
  const typing =
    active instanceof HTMLInputElement && rootEl.contains(active) && active.type !== "button";
  const viewport = window.visualViewport;
  const shrunk = viewport !== null && window.innerHeight - viewport.height > 150;
  bar.classList.toggle("ss-bar-hidden", typing && shrunk);
}

rootEl.addEventListener("focusin", applyKeyboardHide);
rootEl.addEventListener("focusout", () => window.setTimeout(applyKeyboardHide, 0));
window.visualViewport?.addEventListener("resize", applyKeyboardHide);

// ---------------------------------------------------------------------
// Review, confirm, save, result
// ---------------------------------------------------------------------

function enteredItems(): StockItem[] {
  const s = state;
  if (s === undefined) return [];
  const shown = visibleItems(effectiveItems(), s.statusById, s.entries, {
    q: "",
    category: "all",
    status: "entered",
  });
  // Category order, as the list shows it.
  return groupByCategory(shown, s.statusById, s.entries).flatMap((group) => group.items);
}

async function refreshStatusInto(gen: number): Promise<boolean> {
  const s = state;
  if (s === undefined) return false;
  const result = await getOpeningBalanceStatus(s.session.apiBaseUrl, s.session.tenantId);
  if (!isCurrent(gen) || state === undefined) return false;
  if (result.status === 401 || result.status === 403) {
    state.phase = "nopermission";
    renderScreen();
    return false;
  }
  if (result.status !== 200) return false;
  for (const row of result.body.products) {
    state.statusById[row.liquorProductId] = row.history;
  }
  state.startsEmpty = result.body.startsEmpty;
  return true;
}

async function handleReview(): Promise<void> {
  const s = state;
  if (s === undefined || s.checking) return;
  const gen = generation;
  s.checking = true;
  renderBar();
  const ok = await refreshStatusInto(gen);
  if (!isCurrent(gen) || state === undefined) return;
  state.checking = false;
  if (!ok) {
    if (state.phase === "nopermission") return;
    state.notice =
      "Could not check this location's stock history just now. Check the connection and tap Review again. Nothing was written.";
    renderNotice();
    renderBar();
    return;
  }
  // Items that gained history while the person was entering are locked now:
  // drop their entries before anything is written, and say so.
  const lockedNow: string[] = [];
  for (const id of [...state.entries.keys()]) {
    const history = state.statusById[id];
    if (history !== undefined && history !== "none") {
      lockedNow.push(id);
      state.entries.delete(id);
    }
  }
  if (lockedNow.length > 0) {
    persistDraft();
    state.notice =
      `${plural(lockedNow.length, "item got", "items got")} stock history while you were entering ` +
      `and ${lockedNow.length === 1 ? "is" : "are"} now locked. Use a count to change ` +
      `${lockedNow.length === 1 ? "it" : "them"}.`;
  } else {
    state.notice = "";
  }
  state.openId = undefined;
  state.batchId = crypto.randomUUID();
  state.batch = undefined;
  state.unavailable = false;
  const remaining = enteredItems();
  if (remaining.length === 0) {
    state.phase = "ready";
    renderScreen();
    return;
  }
  state.phase = "confirm";
  renderList();
  renderFilterControls();
  renderNotice();
  renderBar();
  renderSheet();
}

function sheetShell(
  title: string,
  subtitle: string,
  body: Node[],
  footer: Node[],
  testid: string,
): HTMLElement {
  const titleEl = h("h2", { id: "ss-sheet-title", tabindex: "-1" }, title);
  return h(
    "div",
    { class: "ss-backdrop", testid: "starting-stock-backdrop" },
    h(
      "div",
      {
        class: "ss-sheet",
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "ss-sheet-title",
        testid,
      },
      h("div", { class: "ss-sheet-h" }, titleEl, subtitle.length > 0 ? h("p", {}, subtitle) : null),
      h("div", { class: "ss-sheet-b" }, ...body),
      h("div", { class: "ss-sheet-f" }, ...footer),
    ),
  );
}

function renderSheet(): void {
  const s = state;
  const slot = rootEl.querySelector<HTMLElement>("#ss-sheet-slot");
  if (s === undefined || slot === null) return;
  if (s.phase === "confirm") {
    slot.replaceChildren(buildConfirmSheet());
  } else if (s.phase === "saving") {
    slot.replaceChildren(buildSavingSheet());
  } else if (s.phase === "result") {
    slot.replaceChildren(buildResultSheet());
  } else {
    slot.replaceChildren();
    return;
  }
  slot.querySelector<HTMLElement>("#ss-sheet-title")?.focus();
}

function tile(value: number, label: string, testid: string): HTMLElement {
  return h("div", { class: "ss-tile" }, h("b", { testid }, String(value)), h("span", {}, label));
}

function summaryRow(
  item: StockItem,
  entry: Entry | undefined,
  tag?: HTMLElement,
  why?: string,
): HTMLElement {
  const display = entryDisplay(item, entry);
  return h(
    "li",
    { testid: "starting-stock-summary-row", "data-id": item.id },
    h(
      "span",
      { class: "ss-sum-name" },
      item.name,
      why !== undefined
        ? h("span", { class: "ss-fail-why" }, why)
        : h("small", {}, item.categoryName),
    ),
    display === null
      ? null
      : h(
          "span",
          { class: "ss-sum-q" },
          display.text,
          h("small", {}, entry?.type === "zero" ? "set as none" : display.sub),
        ),
    tag === undefined ? null : h("span", { class: "ss-sum-st" }, tag),
  );
}

function buildConfirmSheet(): HTMLElement {
  const s = state;
  if (s === undefined) return h("div", {});
  const items = enteredItems();
  const counts = countSummary(effectiveItems(), s.statusById, s.entries);
  const zeros = items.filter((item) => s.entries.get(item.id)?.type === "zero").length;
  return sheetShell(
    `Save ${items.length} starting ${items.length === 1 ? "amount" : "amounts"}?`,
    "Check the list. This writes one starting-stock entry for each item.",
    [
      h(
        "div",
        { class: "ss-tiles" },
        tile(items.length, "items to save", "starting-stock-tile-save"),
        tile(zeros, "set to none", "starting-stock-tile-none"),
        tile(counts.left, "left blank, stay open", "starting-stock-tile-blank"),
      ),
      // UX77 (DEC-288): the $0 cost line sits above the list, so a long list
      // never scrolls it out of sight.
      h(
        "p",
        { class: "ss-lock-note", testid: "starting-stock-lock-note" },
        "After saving, an item’s starting amount is locked. To change it later, run a count. Starting stock has no cost; cost starts with the first delivery, and cost reports read it as $0 until then.",
      ),
      h(
        "ul",
        { class: "ss-sum", testid: "starting-stock-summary" },
        ...items.map((item) => summaryRow(item, s.entries.get(item.id))),
      ),
    ],
    [
      button(
        "Back to the list",
        { class: "btn-secondary", testid: "starting-stock-confirm-back" },
        () => {
          if (state === undefined) return;
          state.phase = "ready";
          renderSheet();
        },
      ),
      button(
        "Save starting stock",
        { class: "btn-primary", testid: "starting-stock-confirm-save" },
        () => void startSaving(enteredItems().map((item) => item.id)),
      ),
    ],
    "starting-stock-confirm",
  );
}

function tagFor(outcome: SaveOutcome | undefined, running: boolean): HTMLElement {
  if (running) return h("span", { class: "ss-tag ss-tag-warn" }, "Saving");
  if (outcome === undefined) return h("span", { class: "ss-tag ss-tag-done" }, "Waiting");
  switch (outcome.kind) {
    case "saved":
      return h("span", { class: "ss-tag ss-tag-ok" }, "Saved");
    case "locked":
      return h("span", { class: "ss-tag ss-tag-bad" }, "Locked");
    case "stop":
    case "unavailable":
    case "retry":
      return h("span", { class: "ss-tag ss-tag-warn" }, "Try again");
    case "fix":
      return h("span", { class: "ss-tag ss-tag-warn" }, "Fix");
  }
}

function buildSavingSheet(): HTMLElement {
  const s = state;
  const batch = s?.batch;
  if (s === undefined || batch === undefined) return h("div", {});
  const done = batch.results.size;
  const percent = batch.total > 0 ? Math.round((done * 100) / batch.total) : 0;
  const byId = new Map(effectiveItems().map((item) => [item.id, item]));
  const rows = batch.order.flatMap((id) => {
    const item = byId.get(id);
    if (item === undefined) return [];
    const li = summaryRow(
      item,
      savedEntries.get(id),
      tagFor(batch.results.get(id), batch.running.has(id)),
    );
    li.dataset["result"] =
      batch.results.get(id)?.kind ?? (batch.running.has(id) ? "saving" : "waiting");
    return [li];
  });
  return sheetShell(
    `Saving ${Math.min(done + batch.running.size, batch.total)} of ${batch.total}`,
    "Keep this screen open. Items already saved stay saved if you stop.",
    [
      h(
        "div",
        {
          class: "ss-meter ss-meter-wide",
          role: "img",
          "aria-label": "Saving progress",
          testid: "starting-stock-progress",
        },
        h("i", { style: `width:${percent}%` }),
      ),
      h("ul", { class: "ss-sum", testid: "starting-stock-saving-list" }, ...rows),
    ],
    [
      button(
        batch.stopping ? "Stopping after this one…" : "Stop after this one",
        { class: "btn-secondary", testid: "starting-stock-stop", disabled: batch.stopping },
        () => {
          if (state?.batch === undefined) return;
          state.batch.stopping = true;
          renderSheet();
        },
      ),
    ],
    "starting-stock-saving",
  );
}

async function startSaving(ids: string[]): Promise<void> {
  const s = state;
  const batchId = s?.batchId;
  if (s === undefined || batchId === undefined || ids.length === 0) return;
  const gen = generation;
  const previous = s.batch;
  const results = previous?.results ?? new Map<string, SaveOutcome>();
  for (const id of ids) results.delete(id);
  const order = previous !== undefined ? previous.order : ids;
  const batch: Batch = {
    id: batchId,
    order,
    results,
    running: new Set(),
    stopping: false,
    total: order.length,
  };
  s.batch = batch;
  s.phase = "saving";
  s.unavailable = false;
  for (const id of order) {
    const entry = s.entries.get(id);
    if (entry !== undefined) savedEntries.set(id, entry);
  }
  renderSheet();

  const byId = new Map(effectiveItems().map((item) => [item.id, item]));
  await runBatch({
    itemIds: ids,
    concurrency: CONCURRENCY,
    shouldStop: () => batch.stopping,
    onStart: (id) => {
      batch.running.add(id);
      if (isCurrent(gen)) renderSheet();
    },
    send: async (id) => {
      const item = byId.get(id);
      const entry = savedEntries.get(id);
      if (item === undefined || entry === undefined) {
        return { kind: "fix", message: "This item is no longer on the list." };
      }
      const result = await recordOpeningBalanceMovement(
        s.session.apiBaseUrl,
        s.session.tenantId,
        buildRequest(item, entry, batchId),
      );
      return classifySaveResponse(result);
    },
    onDone: (id, outcome) => {
      batch.running.delete(id);
      batch.results.set(id, outcome);
      if (outcome.kind === "unavailable") {
        batch.stopping = true;
        s.unavailable = true;
      }
      if (!isCurrent(gen) || state === undefined) return;
      applyOutcome(id, outcome);
      renderSheet();
    },
  });
  if (!isCurrent(gen) || state === undefined) return;
  finishRun();
}

function applyOutcome(id: string, outcome: SaveOutcome): void {
  const s = state;
  if (s === undefined) return;
  if (outcome.kind === "saved") {
    s.statusById[id] = "opening_balance";
    s.entries.delete(id);
    s.rowMessages.delete(id);
    persistDraft();
  } else if (outcome.kind === "locked") {
    s.statusById[id] = "other";
    s.entries.delete(id);
    s.rowMessages.delete(id);
    persistDraft();
  } else if (outcome.kind === "fix") {
    s.rowMessages.set(id, outcome.message);
    if (outcome.reopenAsOunces === true) {
      // The item has no usable bottle size after all: flip the row to the
      // ounces-only editor and drop the entry, which cannot be sent as is.
      s.forcedOunces.add(id);
      s.entries.delete(id);
      persistDraft();
    }
  }
}

function finishRun(): void {
  const s = state;
  const batch = s?.batch;
  if (s === undefined || batch === undefined) return;
  const lostPermission = [...batch.results.values()].some((outcome) => outcome.kind === "stop");
  s.checking = false;
  hooks?.onChanged();
  if (lostPermission) {
    s.phase = "nopermission";
    renderScreen();
    return;
  }
  s.phase = "result";
  renderList();
  renderFilterControls();
  renderBar();
  renderSheet();
}

function buildResultSheet(): HTMLElement {
  const s = state;
  const batch = s?.batch;
  if (s === undefined || batch === undefined) return h("div", {});
  const byId = new Map(effectiveItems().map((item) => [item.id, item]));
  const outcomes = batch.order.map((id) => ({ id, outcome: batch.results.get(id) }));
  const saved = outcomes.filter((row) => row.outcome?.kind === "saved");
  const locked = outcomes.filter((row) => row.outcome?.kind === "locked");
  const retry = outcomes.filter(
    (row) => row.outcome?.kind === "retry" || row.outcome?.kind === "unavailable",
  );
  const fix = outcomes.filter((row) => row.outcome?.kind === "fix");
  const unsent = outcomes.filter((row) => row.outcome === undefined);
  const notSaved = batch.total - saved.length;

  const attention: HTMLElement[] = [];
  for (const row of [...locked, ...fix, ...retry]) {
    const item = byId.get(row.id);
    if (item === undefined || row.outcome === undefined) continue;
    attention.push(
      summaryRow(item, savedEntries.get(row.id), tagFor(row.outcome, false), row.outcome.message),
    );
  }
  const body: Node[] = [
    h(
      "div",
      { class: "ss-tiles" },
      tile(saved.length, "saved", "starting-stock-result-saved"),
      tile(locked.length, "got history first", "starting-stock-result-locked"),
      tile(notSaved - locked.length, "not saved yet", "starting-stock-result-open"),
    ),
  ];
  if (s.unavailable) {
    body.push(
      h(
        "p",
        { class: "ss-fail-why", testid: "starting-stock-unavailable" },
        "This location cannot save stock changes right now. Nothing was written for the items below. Try again in a few minutes.",
      ),
    );
  }
  if (attention.length > 0) {
    body.push(
      h("div", { class: "ss-h3" }, "Needs attention"),
      h("ul", { class: "ss-sum", testid: "starting-stock-attention" }, ...attention),
    );
  }
  if (unsent.length > 0) {
    body.push(
      h(
        "p",
        { class: "ss-fail-why", testid: "starting-stock-unsent" },
        `${plural(unsent.length, "item was", "items were")} not sent. ${unsent.length === 1 ? "It is" : "They are"} still entered on the list.`,
      ),
    );
  }
  if (saved.length > 0) {
    body.push(
      h("div", { class: "ss-h3" }, "Saved"),
      h(
        "ul",
        { class: "ss-sum", testid: "starting-stock-saved" },
        h(
          "li",
          {},
          h(
            "span",
            { class: "ss-sum-name" },
            `${plural(saved.length, "item now has", "items now have")} starting stock`,
            h("small", {}, "They show as Starting stock set in the list."),
          ),
          h("span", { class: "ss-sum-st" }, h("span", { class: "ss-tag ss-tag-ok" }, "Saved")),
        ),
      ),
    );
  }
  const footer: HTMLElement[] = [
    button(
      "Back to the list",
      {
        class: retry.length > 0 ? "btn-secondary" : "btn-primary",
        testid: "starting-stock-result-back",
      },
      () => {
        if (state === undefined) return;
        const fixFirst = fix.find((row) => state?.forcedOunces.has(row.id));
        state.phase = "ready";
        state.batch = undefined;
        state.batchId = undefined;
        savedEntries.clear();
        state.openId = fixFirst?.id;
        renderScreen();
      },
    ),
  ];
  if (locked.length > 0) {
    footer.push(
      button(
        "Start a count",
        { class: "btn-secondary", testid: "starting-stock-result-count" },
        () => {
          closeStartingStockScreen();
          hooks?.goToCounts();
        },
      ),
    );
  }
  if (retry.length > 0) {
    footer.push(
      button(
        `Try again (${retry.length})`,
        { class: "btn-primary", testid: "starting-stock-result-retry" },
        () => void startSaving(retry.map((row) => row.id)),
      ),
    );
  }
  return sheetShell(
    `${saved.length} of ${batch.total} saved`,
    notSaved === 0
      ? "All saved. Starting stock is locked for these items. To change one later, use a count."
      : `${plural(notSaved, "item did", "items did")} not save. Nothing was written for ${notSaved === 1 ? "it" : "them"}.`,
    body,
    footer,
    "starting-stock-result",
  );
}
